import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CategoryUtils, DossierUtils, Feature, translationKeys } from '../../../../../core';
import {
  ArticleCategoryDTO,
  DossierDto,
  MaterialCategoryDTO,
  MixtureCategoryDTO,
  OtherArticleIdentifierDto,
  OtherArticleNamesDto,
  ProductNodeDto,
  ResourceSuiMapDTO
} from '@scip/api';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DossierService, DownloadService, EnumsService, EnvironmentConfigurationService, FeatureService } from '../../../../../services';
import { SelectItem } from 'primeng/api';
import { Subscription } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';
import { WhitespaceValidatorDirective, UuidValidatorDirective } from '../../../../../shared';

@Component({
  selector: 'scip-product-form',
  templateUrl: './product-form.component.html',
  styleUrls: ['./product-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductFormComponent implements OnInit, OnDestroy {
  @Input() set product(val: ProductNodeDto) {
    this._product = val;
    this.dossier = this._product?.dossier;
    // TODO: refactor this after fix for select items with translations
    if (!this._inEUTypes) {
      this.enumService.valuesTranslated.then(() => {
        this._inEUTypes = [...this.enumService.inEUTypes];
        this.setFormValue(val);
      });
    } else {
      this.setFormValue(val);
    }
  }

  @Input() set disabled(val: boolean) {
    if (val) {
      this.productForm.disable();
    }
  }

  @Output() private readonly formChange: EventEmitter<ProductNodeDto> = new EventEmitter<ProductNodeDto>();

  canEditArticleIdentifier = false;
  canEditSupplierReference = false;
  displayArticleCategorySelection = false;
  displayMaterialCategorySelection = false;
  displayMixtureCategorySelection = false;
  displayOtherArticleIdentifierSelection = false;
  displayOtherNameSelection = false;
  feature = Feature;

  inEUTypeSuggestions: SelectItem[] = [];
  showDossierInfo = false;
  showOpenInECHABtn = false;
  showOtherMatCatTextField = false;
  showSuiRolledUp = false;
  showVdaMatCat = false;
  showVdaMixCat = false;
  allowForceResubmission = false;
  suiRolledUp: ResourceSuiMapDTO[] = [];
  dossier: DossierDto;

  forceResubmission: FormControl = new FormControl({ value: false });
  articleCategories: FormControl = new FormControl([]);
  dossierStatus: FormControl = new FormControl('');
  materialCategories: FormControl = new FormControl([]);
  mixtureCategories: FormControl = new FormControl([]);
  otherArticleIdentifiers: FormControl = new FormControl([]);
  otherArticleNames: FormControl = new FormControl([]);
  primaryArticleIdentifier: FormControl = new FormControl([]);
  productionInEU: FormControl = new FormControl([], [Validators.required, Validators.minLength(1)]);
  productionInEuSuggestions: FormControl = new FormControl([], [Validators.required, Validators.minLength(1)]);
  vdaMaterialCategories: FormControl = new FormControl({ value: '', disabled: true });
  vdaMixtureCategories: FormControl = new FormControl({ value: '', disabled: true });
  imdsScipNumber: FormControl = new FormControl([], [Validators.maxLength(255), WhitespaceValidatorDirective.whitespace, UuidValidatorDirective.uuid]);
  imdsScipSubmissionNumber: FormControl = new FormControl({ value: '123' });
  isValidImdsScipNumber = false;
  useImdsScipNumber: FormControl = new FormControl({ value: false });

  productForm: FormGroup = this.fb.group({
    productType: '',
    name: '',
    primaryArticleIdentifierValue: this.primaryArticleIdentifier,
    otherArticleIdentifiers: this.otherArticleIdentifiers,
    otherArticleNames: this.otherArticleNames,
    productionInEU: this.productionInEU,
    articleCategories: this.articleCategories,
    materialCategories: this.materialCategories,
    vdaMaterialCategories: this.vdaMaterialCategories,
    vdaMixtureCategories: this.vdaMixtureCategories,
    otherMatCatText: [''],
    mixtureCategories: this.mixtureCategories,
    imdsScipNumber: this.imdsScipNumber,
    imdsScipSubmissionNumber: this.imdsScipSubmissionNumber,
    isValidImdsScipNumber: this.isValidImdsScipNumber,
    useImdsScipNumber: this.useImdsScipNumber,
    provideSafeUseInstruction: [''],
    customSUIText: [''],
    forceResubmission: this.forceResubmission,
    dossier: this.fb.group({
      scipNumber: [''],
      org: this.fb.group({
        name: ['']
      }),
      submissionNumber: [''],
      reportUrl: [''],
      status: this.dossierStatus
    })
  });

  translationKeys = translationKeys;

  private _inEUTypes: SelectItem[];
  private _product: ProductNodeDto;
  private subscription: Subscription;

  constructor(
    private fb: FormBuilder,
    private enumService: EnumsService,
    private downloadService: DownloadService,
    private dossierService: DossierService,
    private environmentConfigurationService: EnvironmentConfigurationService,
    private featureService: FeatureService
  ) {}

  ngOnInit(): void {
    this.canEditSupplierReference = this.featureService.isActive(Feature.EDIT_SUPPLIERREFERENCE);
    this.canEditArticleIdentifier = this.environmentConfigurationService.environment.canEditArticleIdentifier;
    if (this.canEditArticleIdentifier) {
      this.primaryArticleIdentifier.setValidators(Validators.required);
      this.primaryArticleIdentifier.updateValueAndValidity();
    }
  }

  closeArticleCategoryDialog(): void {
    this.displayArticleCategorySelection = false;
  }

  closeDialog(): void {
    this.displayOtherArticleIdentifierSelection = false;
  }

  closeMaterialCategoryDialog(): void {
    this.displayMaterialCategorySelection = false;
  }

  closeMixtureCategoryDialog(): void {
    this.displayMixtureCategorySelection = false;
  }

  closeOtherNameDialog(): void {
    this.displayOtherNameSelection = false;
  }

  downloadDossier(isInDownload: boolean): void {
    if (!isInDownload && this._product.dossier) {
      this.downloadService.downloadDossier(this._product.dossier).subscribe();
    }
  }

  findInEUType(productionInEu: string): SelectItem {
    return this._inEUTypes.find(({ value }) => value === productionInEu);
  }

  filterInEUTypes(event: any): void {
    this.inEUTypeSuggestions = this.enumService.filterInEUTypes(event);
  }

  openInformationArticleCategoryDialog(): void {
    this.displayArticleCategorySelection = true;
  }

  openLinks(): void {
    this.dossierService.openDossierEchaLinks([this._product.dossier]);
  }

  openMaterialCategoryDialog(): void {
    this.displayMaterialCategorySelection = true;
  }

  openMixtureCategoryDialog(): void {
    this.displayMixtureCategorySelection = true;
  }

  openOtherArticleIdentifierDialog(): void {
    this.displayOtherArticleIdentifierSelection = true;
  }

  openOtherNameDialog(): void {
    this.displayOtherNameSelection = true;
  }

  productionInEUChanged({ value }: SelectItem): void {
    this.productionInEU.setValue(value);
    this.productionInEU.markAsDirty();
    this.productionInEU.updateValueAndValidity();
  }

  saveArticleCategory(categories: ArticleCategoryDTO[]): void {
    this.updateValueFromDialog(this.articleCategories, categories);
    this.displayArticleCategorySelection = false;
  }

  saveMaterialCategory(categories: MaterialCategoryDTO[]): void {
    this.updateValueFromDialog(this.materialCategories, categories);
    this.showOtherMatCatTextField = CategoryUtils.checkForCategoryId([...categories], '1342');
    this.displayMaterialCategorySelection = false;
  }

  saveMixtureCategory(categories: MixtureCategoryDTO[]): void {
    this.updateValueFromDialog(this.mixtureCategories, categories);
    this.displayMixtureCategorySelection = false;
  }

  saveOtherArticleIdentifiers(identifiers: OtherArticleIdentifierDto[]): void {
    this.updateValueFromDialog(this.otherArticleIdentifiers, identifiers);
    this.displayOtherArticleIdentifierSelection = false;
  }

  saveOtherNames(otherNames: OtherArticleNamesDto[]): void {
    this.updateValueFromDialog(this.otherArticleNames, otherNames);
    this.displayOtherNameSelection = false;
  }

  updateShowOtherMatCatTextField(): void {
    this.showOtherMatCatTextField = CategoryUtils.checkForCategoryId(this.materialCategories.value, '1342');
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private setFormValue(product: ProductNodeDto): void {
    const productionInEUMatch: SelectItem = this._inEUTypes?.find(({ value }) => value === product.productionInEU);
    this.productionInEuSuggestions.setValue(productionInEUMatch);

    const vdaMaterialCategories = {
      value: product.vdaMaterialCategories,
      disabled: true
    };

    const vdaMixtureCategories = {
      value: product.vdaMixtureCategories,
      disabled: true
    };

    const { showSUIRolledUp, showVDACats, allowForceResubmission } = this.environmentConfigurationService.environment;

    this.showDossierInfo = !!product.dossier;
    this.showOpenInECHABtn = product.dossier && DossierUtils.canOpenECHAPortal(product.dossier);
    this.showSuiRolledUp = showSUIRolledUp && product.resourceSuiMaps !== undefined;
    this.suiRolledUp = product.resourceSuiMaps;
    this.showVdaMatCat = product.vdaMaterialCategories !== undefined && showVDACats;
    this.showVdaMixCat = product.vdaMixtureCategories !== undefined && showVDACats;
    this.showOtherMatCatTextField = CategoryUtils.checkForCategoryId(product.materialCategories, '1342');
    this.allowForceResubmission = product.forceResubmission !== undefined && allowForceResubmission;

    this.productForm.get('dossier').disable();
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.productForm.reset({
      ...product,
      vdaMaterialCategories,
      vdaMixtureCategories
    });
    this.subscribeOnFormChange();
  }

  private subscribeOnFormChange(): void {
    this.subscription = new Subscription();
    const subscription = this.productForm.valueChanges
      .pipe(
        debounceTime(200),
        tap((formValue: ProductNodeDto) => {
          this.formChange.emit({
            ...this._product,
            ...formValue
          });
        })
      )
      .subscribe();
    this.subscription.add(subscription);
  }

  private updateValueFromDialog(control: FormControl, value: unknown[]): void {
    control.setValue([...value]);
    control.markAsDirty();
    control.updateValueAndValidity();
  }
}
